import { Briefcase, DotsThree, DownloadSimple, Plus } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { Organization } from "~/api/governanceTypes";
import {
  useCreateOrganization,
  useDeleteOrganization,
  useOrganizations,
  useUpdateOrganization,
} from "~/api/queries";
import { Pill, Row, Rows } from "~/features/automation/components";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog, Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Menu } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { downloadCsv, downloadJson } from "~/foundation/utils/exportFile";

function OrgDialog({ org, onClose }: { org?: Organization; onClose: () => void }) {
  const { t } = useTranslation();
  const createOrg = useCreateOrganization();
  const updateOrg = useUpdateOrganization();
  const [name, setName] = useState(org?.name ?? "");
  const [domain, setDomain] = useState(org?.domain ?? "");
  const isEdit = Boolean(org);
  const busy = createOrg.isPending || updateOrg.isPending;

  const submit = () => {
    if (!name.trim()) {
      return;
    }
    const input = { name: name.trim(), domain: domain.trim() || undefined };
    if (isEdit && org) {
      updateOrg.mutate(
        { id: org.id, ...input },
        {
          onSuccess: () => {
            onClose();
            toast.success(t("Organization saved"));
          },
        },
      );
    } else {
      createOrg.mutate(input, {
        onSuccess: () => {
          onClose();
          toast.success(t("Organization created"));
        },
      });
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={isEdit ? t("Edit organization") : t("New organization")}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={!name.trim() || busy} onClick={submit}>
            {isEdit ? t("Save") : t("Create")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label={t("Name")}
          fullWidth
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Input
          label={t("Domain")}
          fullWidth
          value={domain}
          onChange={(event) => setDomain(event.target.value)}
        />
      </div>
    </Dialog>
  );
}

export function OrganizationsSection() {
  const { t } = useTranslation();
  const { data: orgs, isLoading } = useOrganizations();
  const deleteOrg = useDeleteOrganization();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Organization | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Organization | null>(null);

  const exportCsv = () => {
    downloadCsv(
      "organizations.csv",
      ["ID", "Name", "Domain", "Created At"],
      (orgs ?? []).map((o) => [o.id, o.name, o.domain ?? "", o.createdAt]),
    );
  };
  const exportJson = () => downloadJson("organizations.json", orgs ?? []);

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end gap-2">
        <Button variant="neutral" icon={DownloadSimple} onClick={exportCsv}>
          {t("Export CSV")}
        </Button>
        <Button variant="neutral" icon={DownloadSimple} onClick={exportJson}>
          {t("Export JSON")}
        </Button>
        <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
          {t("New organization")}
        </Button>
      </div>

      {!orgs?.length ? (
        <Empty icon={Briefcase} message={t("No organizations yet")} />
      ) : (
        <Rows>
          {orgs.map((org) => (
            <Row key={org.id}>
              <Briefcase size={18} aria-hidden className="shrink-0 text-text-tertiary" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-text">{org.name}</p>
                <p className="truncate text-small text-text-tertiary">{org.domain ?? "—"}</p>
              </div>
              <Pill tone="neutral">{org.planTier}</Pill>
              <Menu
                items={[
                  { type: "button", title: t("Edit…"), onClick: () => setEditing(org) },
                  {
                    type: "button",
                    title: t("Delete…"),
                    dangerous: true,
                    onClick: () => setConfirmDelete(org),
                  },
                ]}
                trigger={<IconButton icon={DotsThree} aria-label={t("Actions")} />}
              />
            </Row>
          ))}
        </Rows>
      )}

      {creating ? <OrgDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <OrgDialog org={editing} onClose={() => setEditing(null)} /> : null}
      <ConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t("Delete {{name}}?", { name: confirmDelete?.name ?? "" })}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={deleteOrg.isPending}
        onConfirm={() => {
          if (!confirmDelete) {
            return;
          }
          deleteOrg.mutate(confirmDelete.id, {
            onSuccess: () => {
              setConfirmDelete(null);
              toast.success(t("Organization deleted"));
            },
          });
        }}
      />
    </div>
  );
}
